import React, { useState } from 'react';
import { studentDeletionAPI } from '../services/api';
import { getStoredAuthToken } from '../utils/authStorage';
import {
  BULK_DELETE_LIMIT,
  BULK_DELETE_LIMIT_MESSAGE
} from '../utils/bulkStudentSelection';

const assertDeletionResponse = (response) => {
  if (!response?.data?.success) {
    throw new Error(response?.data?.message || 'Deletion failed');
  }
  return response.data?.data;
};

const StudentDeletionModal = ({
  isOpen,
  onClose,
  student,
  onSuccess,
  onError,
  isBulk = false,
  selectedStudents = []
}) => {
  const [deletionType, setDeletionType] = useState('soft');
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [backupData, setBackupData] = useState(true);
  const [notifyRecruiters, setNotifyRecruiters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarnings([]);

    try {
      if (!getStoredAuthToken()) {
        throw new Error('No authentication token found. Please login again.');
      }

      const deletionData = {
        reason,
        force,
        backupData,
        notifyRecruiters
      };

      let result;
      if (isBulk) {
        const studentIds = selectedStudents.map((s) => s.id).filter(Boolean);
        if (studentIds.length === 0) {
          throw new Error('No valid student IDs selected for deletion.');
        }
        if (studentIds.length > BULK_DELETE_LIMIT) {
          throw new Error(BULK_DELETE_LIMIT_MESSAGE);
        }

        const batchPayload = { studentIds, ...deletionData };
        if (deletionType === 'soft') {
          try {
            const response = await studentDeletionAPI.bulkSoftDelete(batchPayload);
            result = assertDeletionResponse(response);
          } catch (err) {
            if (err.response?.status !== 404) {
              throw err;
            }
            for (const studentId of studentIds) {
              await studentDeletionAPI.softDelete(studentId, deletionData);
            }
            result = { deletedCount: studentIds.length };
          }
        } else {
          const response = await studentDeletionAPI.bulkDelete(batchPayload);
          result = assertDeletionResponse(response);
        }
      } else if (deletionType === 'soft') {
        const response = await studentDeletionAPI.softDelete(student.id, deletionData);
        result = assertDeletionResponse(response);
      } else {
        const response = await studentDeletionAPI.hardDelete(student.id, deletionData);
        result = assertDeletionResponse(response);
      }

      onSuccess(result);
      onClose();
    } catch (err) {
      console.error('❌ Deletion error:', err);

      let errorMessage = 'Deletion failed';
      if (err.message?.includes('authentication token')) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. Admin role required.';
      } else if (err.response?.status === 404) {
        errorMessage = err.response?.data?.message || 'Delete endpoint not found. Restart the backend server and try again.';
      } else if (err.response?.status === 409) {
        errorMessage = err.response?.data?.message || 'Some students have active enrollments or placements.';
      } else if (err.response?.status === 500) {
        errorMessage = err.response?.data?.message || err.response?.data?.error || 'Server error. Please try again later.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);

      if (err.response?.data?.code === 'ACTIVE_ENROLLMENTS' || err.response?.data?.code === 'HAS_DEPENDENCIES') {
        setWarnings([
          'Some students have active course enrollments or placement records',
          'Use Soft Delete instead of Hard Delete, or enable Force delete for hard delete',
          'Force delete only if absolutely necessary'
        ]);
      } else if (err.response?.data?.code === 'BULK_LIMIT_EXCEEDED') {
        setWarnings([BULK_DELETE_LIMIT_MESSAGE]);
      }

      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setReason('');
    setForce(false);
    setError('');
    setWarnings([]);
    onClose();
  };

  if (!isOpen) return null;

  const studentCount = isBulk ? selectedStudents.length : 1;
  const studentName = isBulk ? `${studentCount} students` : `${student.firstName} ${student.lastName}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-red-600">
            {isBulk ? 'Bulk Delete Students' : 'Delete Student'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center mb-2">
            <span className="text-red-600 text-2xl mr-2">⚠️</span>
            <span className="font-semibold text-red-800">
              Warning: This action will {deletionType === 'soft' ? 'deactivate' : 'permanently delete'} {studentName}
            </span>
          </div>
          <p className="text-red-700 text-sm">
            {deletionType === 'soft'
              ? 'The student will be marked as inactive but data will be preserved.'
              : 'This action cannot be undone. All student data will be permanently removed.'
            }
          </p>
          {isBulk && (
            <p className="text-red-700 text-sm mt-2">
              Maximum {BULK_DELETE_LIMIT} students per delete. Select another batch after this completes.
            </p>
          )}
        </div>

        {warnings.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <ul className="text-yellow-700 text-sm list-disc list-inside">
              {warnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deletion Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="soft"
                  checked={deletionType === 'soft'}
                  onChange={(e) => setDeletionType(e.target.value)}
                  className="mr-2"
                  disabled={loading}
                />
                <span className="text-sm">
                  <strong>Soft Delete</strong> - Mark as inactive (recommended)
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="hard"
                  checked={deletionType === 'hard'}
                  onChange={(e) => setDeletionType(e.target.value)}
                  className="mr-2"
                  disabled={loading}
                />
                <span className="text-sm">
                  <strong>Hard Delete</strong> - Permanently remove all data
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Deletion *
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please provide a reason for deletion..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-3">
            <h3 className="font-medium text-gray-700">Options</h3>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={backupData}
                onChange={(e) => setBackupData(e.target.checked)}
                className="mr-2"
                disabled={loading}
              />
              <span className="text-sm">Create backup of student data</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={notifyRecruiters}
                onChange={(e) => setNotifyRecruiters(e.target.checked)}
                className="mr-2"
                disabled={loading}
              />
              <span className="text-sm">Notify recruiters (if student was shared)</span>
            </label>

            {deletionType === 'hard' && (
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  className="mr-2"
                  disabled={loading}
                />
                <span className="text-sm text-red-600">
                  <strong>Force delete</strong> - Ignore dependencies (dangerous)
                </span>
              </label>
            )}
          </div>

          {error && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-md">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !reason.trim() || (isBulk && studentCount > BULK_DELETE_LIMIT)}
              className={`px-4 py-2 rounded-md text-white ${
                deletionType === 'soft'
                  ? 'bg-yellow-600 hover:bg-yellow-700'
                  : 'bg-red-600 hover:bg-red-700'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? 'Processing...' : `${deletionType === 'soft' ? 'Deactivate' : 'Delete'} ${studentName}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentDeletionModal;
